import React, { ReactNode, useMemo } from 'react';
import { FlatList, StyleProp, StyleSheet, TouchableOpacity, View, ViewStyle } from 'react-native';
import { useField } from 'formik';
import FontAwesome6 from 'react-native-vector-icons/FontAwesome6';
import { AppPickerSelect, FormField, FormFieldPickerProps } from '../FormField';
import AppText, { Mode } from '../AppText';
import { LIGHT_GRAY, PRIMARY_COLOR } from '../../../styles/appColor';

export type MultiSelectFieldProps<T, V> = Omit<
  FormFieldPickerProps<T>,
  'defaultValue' | 'data' | 'onChangeText' | 'onChangeText' | 'title'
> & {
  name: string;
  title?: string;
  required?: boolean;
  style?: StyleProp<ViewStyle>;
  emptyText?: string;
  data: T[];
  valueExtractor: (item: T) => V;
  labelExtractor: (item: T) => string;
  renderItem: (item: T) => ReactNode;
};

export function MultiSelectField<T, V extends string | number>({
  name,
  title,
  required,
  style,
  displayOnly,
  data,
  emptyText,
  valueExtractor,
  labelExtractor,
  renderItem,
  ...rest
}: MultiSelectFieldProps<T, V>) {
  const [field, meta, helper] = useField<V[]>(name);

  const handleChangeText = (value: V) => {
    helper.setValue([...field.value, value]);
  };

  const onRemove = (item: T) => {
    helper.setValue(field.value.filter((e) => e !== valueExtractor(item)));
  };

  const filteredData = useMemo(() => {
    return data.filter((e) => !field.value.includes(valueExtractor(e)));
  }, [data, field.value]);

  const content = (
    <View>
      <AppPickerSelect<V>
        {...rest}
        data={filteredData.map((e) => ({ value: valueExtractor(e), label: labelExtractor(e) }))}
        displayOnly={displayOnly}
        onChangeText={handleChangeText}
      />
      <FlatList<V>
        data={field.value}
        keyExtractor={(e) => e.toString()}
        renderItem={(args) => {
          const item = data.find((e) => valueExtractor(e) === args.item);

          return item ? (
            <View style={styles.itemContainer}>
              <View>{renderItem(item)}</View>
              {!displayOnly && (
                <TouchableOpacity onPress={() => onRemove(item)}>
                  <FontAwesome6 size={18} name="trash-alt" color={PRIMARY_COLOR} />
                </TouchableOpacity>
              )}
            </View>
          ) : (
            <></>
          );
        }}
        ListEmptyComponent={emptyText ? <AppText style={styles.emptyText}>{emptyText}</AppText> : undefined}
      />
    </View>
  );

  if (!title) {
    return content;
  } else {
    return (
      <FormField style={style} title={required ? `${title}*` : title}>
        <View style={{ opacity: !displayOnly ? 1 : 0.5 }}>{content}</View>
        {meta.touched && meta.error && <AppText mode={Mode.ERROR}>{meta.error}</AppText>}
      </FormField>
    );
  }
}

const styles = StyleSheet.create({
  itemContainer: {
    flexDirection: 'row',
    gap: 8,
    width: '100%',
    justifyContent: 'space-between',
    maxWidth: 300,
    alignItems: 'center',
  },
  emptyText: {
    textAlign: 'center',
    color: LIGHT_GRAY,
  },
});
